import { useRef } from "react"
import PropertyBidCard from "@/Components/PropertyBigCard"
import { useAppDispatch } from "@/store/hooks"
import { removeNotification } from "@/store/globalSlice"
import { allPieces } from "@/utils/data"
import { mapDesigns } from "@mono/mapDesigns"
import PlayerListItemInfoSmall from "@/Components/PlayerInfoSmall"
import {
  getPlayerByPlayerOrder,
  getUserByPlayerOrder,
} from "@/utils/gameHelpers"
import { TradePanelSmall } from "@/Components/TradePanelSmall"
import {
  dividePriceIntoBills,
  runTradeMinimizeAnimation,
} from "@/utils/animationHelpers"

// import mrMonopolyOffering from "@/images/mr-monopoly/offering.png"
// import mrMonopolyCreatingOffer from "@/images/mr-monopoly/creating-offer.png"
// import mrMonopolySad from "@/images/mr-monopoly/sad.png"
// import mrMonopolyAccepting from "@/images/mr-monopoly/accepting.png"
import classNames from "classnames"
const eventTypeToTitleObject: Record<ITradeEvent["status"], string> = {
  accepted: "Successful trade",
  declined: "Trade declined",
  offered: "Trade being offered",
  "createing-offer": "",
}

const eventTypeToImageObject: Record<ITradeEvent["status"], JSX.Element> = {
  accepted: (
    <img
      className="absolute top-0 -translate-y-[70%] h-24 -translate-x-1/2 left-1/2"
      src=""
    />
  ),
  declined: (
    <img
      className="absolute top-0 -translate-y-[100%] h-24 -translate-x-1/2 left-1/2"
      src=""
    />
  ),
  offered: (
    <img
      className="absolute top-0 -translate-y-[100%] h-20 -translate-x-1/2 left-1/2"
      src=""
    />
  ),
  "createing-offer": <></>,
}

export const TradeNotification: React.FC<TradeNotificationProps> = ({
  id,
  takenProperties,
  givenProperties,
  moneyTaken,
  moneyGiven,
  offeringPlayerOrder,
  offeredPlayerOrder,
  eventType,
  rejectedAt,
}) => {
  const dispatch = useAppDispatch()
  const rootRef = useRef<HTMLDivElement>(null)
  const minimizingRef = useRef(false)
  const netMoney = moneyTaken - moneyGiven
  const isOffered = eventType === "offered"
  const isDeclined = eventType === "declined"
  // Only a declined trade carrying a rejectedAt was reopened from the trades
  // panel's history — a live decline has nowhere to minimize back into.
  const isRejectedHistory = isDeclined && !!rejectedAt
  // A dismissed trade-detail modal minimizes into its own row in the trades
  // panel's history (instead of vanishing), so players learn the details stay
  // reviewable there.
  const close = () => {
    const historyKind = eventType === "accepted"
      ? "completed"
      : isRejectedHistory
      ? "rejected"
      : null
    if (historyKind && rootRef.current) {
      if (minimizingRef.current) return
      minimizingRef.current = true
      runTradeMinimizeAnimation(
        rootRef.current,
        () => dispatch(removeNotification("trade")),
        id,
        historyKind
      )
      return
    }
    dispatch(removeNotification("trade"))
  }
  return (
    <>
      <div
        ref={rootRef}
        data-testid={
          isRejectedHistory
            ? "notification.trade-rejected"
            : `notification.trade-${eventType}`
        }
        className={classNames(
          "fixed rounded-3xl top-1/2 left-1/2 gap-10 -translate-y-1/2 font-poppins -translate-x-1/2 w-1/3 bg-opacity-80 backdrop-blur-xl bg-[rgb(0,0,0)] flex flex-col items-center py-8 z-20 overflow-hidden trade-notification-pop-in",
          {
            "shadow-tradeDeclinedNotification ring-2 ring-red-500/50": isDeclined,
            "shadow-tradeAcceptedNotification": eventType === "accepted",
            "shadow-tradeOfferedNotification": isOffered,
            "trade-offered-glow": isOffered,
          }
        )}
      >
        {isOffered && (
          <div className="absolute inset-0 rounded-3xl pointer-events-none trade-shimmer-border" />
        )}

        <div
          className="absolute top-2 right-2 z-10 flex h-[44px] w-[44px] cursor-pointer items-center justify-center rounded-full text-white transition-colors duration-150 hover:bg-white/10"
          onClick={close}
        >
          <i className="fa-xmark fa-solid text-[22px] text-white transition-colors duration-150 hover:text-red-500"></i>
        </div>
        <div
          className={classNames("text-4xl font-bold relative", {
            "trade-title-breathe": isOffered,
            "text-red-400": isDeclined,
            "text-white": !isDeclined,
          })}
        >
          {eventTypeToTitleObject[eventType]}
        </div>
        {/* Same layout as the "Successful trade" view, so the stamp is what
            says nothing actually changed hands here. */}
        {isDeclined && (
          <div className="flex flex-col items-center gap-2 -mt-6">
            <div className="flex items-center gap-2 rounded-full border-2 border-red-500/60 bg-red-500/20 px-4 py-1 font-poppins font-bold text-sm uppercase tracking-[0.2em] text-red-300">
              <i className="fa-solid fa-ban"></i>
              Rejected
            </div>
            <div className="font-poppins text-xs text-white/50">
              This offer was turned down — nothing changed hands.
            </div>
          </div>
        )}
        {isOffered && (
          <div className="flex items-center gap-3 -mt-4">
            {[0, 1, 2].map((dot) => (
              <span
                key={dot}
                className="h-3 w-3 rounded-full bg-amber-400 trade-dot-pulse"
                style={{ animationDelay: `${dot * 0.25}s` }}
              />
            ))}
          </div>
        )}

        {/* Each column is one player's side of the deal: what they end up with.
            The offering player receives what they *took* from the other side,
            and hands over what they gave — so the arrays cross over here. */}
        <div className="relative grid grid-cols-[1fr_auto_1fr] items-center w-full place-items-center gap-4">
          <TradePanelSmall
            playerOrder={offeringPlayerOrder}
            receivedProperties={takenProperties}
            money={netMoney}
          ></TradePanelSmall>

          {isOffered ? (
            <div className="flex flex-col items-center gap-3">
              <i className="fa-solid fa-arrow-right text-white/50 text-xl" />
              <i className="fa-solid fa-arrow-left text-white/50 text-xl" />
            </div>
          ) : (
            <div />
          )}

          <TradePanelSmall
            playerOrder={offeredPlayerOrder}
            receivedProperties={givenProperties}
            money={-netMoney}
          ></TradePanelSmall>
        </div>
      </div>
    </>
  )
}
